<!-- @format -->
<template>
    <div class="form-widget">
        <loading-card :is-loading="isLoading || isSaving" />
        <div class="widget-contents-container">
            <div class="widget-header">
                <div class="header-text">
                    <h3>{{ widgetTitle }}</h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="!isLoading">
                    <form-starting-step
                        v-if="currentStep === 'startingPoint'"
                        :template-options="templateOptions"
                        :selected-template-option="selectedTemplateOption"
                        @input="handleInputTemplateOption"
                    />
                    <form-existing-template-step
                        v-if="currentStep === 'existingTemplate'"
                        :form="formConfig"
                        :client-id="clientId"
                    />
                    <form-settings-step
                        v-if="currentStep === 'settings'"
                        :account-id="accountId"
                        :client-id="clientId"
                        :email-text-options="emailTextOptions"
                        :form-options="formOptions"
                        :is-loading="isSaving"
                        :form-config="formConfig"
                    />
                </div>
            </div>
            <div class="widget-footer">
                <app-button class="cancel-btn transparent-btn" size="normal" @click="closeWidget">
                    Cancel
                </app-button>
                <div class="actions-container">
                    <app-button
                        v-if="hasBackAction && !isEditMode && !isReturnDisabled"
                        class="secondary-btn"
                        @click="goBack"
                    >
                        <span class="fa fa-arrow-left"></span>
                        Go Back
                    </app-button>
                    <app-button :disabled="isNextDisabled || isLoading || isSaving" @click="goNext">
                        Next Step
                        <span class="fa fa-arrow-right"></span>
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus.js';
import { FormService } from '@/scripts/services/form/form-service';

const LoadingCard = () => import('@/components/common/loading-card');
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const FormSettingsStep = () =>
    import('@/components/sliding-widgets/form-widget/form-settings-step');
const FormStartingStep = () =>
    import('@/components/sliding-widgets/form-widget/form-starting-step');
const FormExistingTemplateStep = () =>
    import('@/components/sliding-widgets/form-widget/form-existing-template-step');

export default {
    name: 'form-widget',

    components: {
        'loading-card': LoadingCard,
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'form-settings-step': FormSettingsStep,
        'form-starting-step': FormStartingStep,
        'form-existing-template-step': FormExistingTemplateStep,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,

            isSaving: false,

            currentStep: 'startingPoint',

            lastActiveStep: null,

            isReturnDisabled: false,

            defaultFormConfig: {
                name: '',
                title: '',
                description: '',
                categories: [],
                action: { id: 1, name: 'Display Text' },
                actionMessage: 'Thank you for your submission!',
                actionRedirect: '',

                emails: [
                    {
                        id: this.$helpers.createId('email'),
                        name: '',
                    },
                ],

                emailSubject: '',
                isPlainTextNotification: false,
                formSubmissionLists: [],
                configuration: '',
            },

            formConfig: {
                name: '',
                title: '',
                description: '',
                categories: [],
                action: { id: 1, name: 'Display Text' },
                actionMessage: 'Thank you for your submission!',
                actionRedirect: '',

                emails: [
                    {
                        id: this.$helpers.createId('email'),
                        name: '',
                    },
                ],

                emailSubject: '',
                isPlainTextNotification: false,
                formSubmissionLists: [],
                configuration: '',
            },

            formOptions: [
                { id: 1, name: 'Display Text' },
                { id: 2, name: 'Redirect' },
            ],

            emailTextOptions: [
                { id: 2, name: 'No', selected: true },
                { id: 1, name: 'Yes', selected: false },
            ],

            selectedTemplateOption: null,

            templateOptions: [
                {
                    imageUrl: require('@/assets/images/forms-fresh.png'),
                    name: 'Start Fresh',
                    value: 'settings',
                },
                {
                    imageUrl: require('@/assets/images/forms-template.png'),
                    name: 'Existing Template',
                    value: 'existingTemplate',
                },
            ],

            steps: {
                startingPoint: {
                    title: 'Create a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep(this.selectedTemplateOption.value);
                            },
                        },
                    },
                },

                existingTemplate: {
                    title: 'Select a Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.goToStep('settings');
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.formConfig = {
                                    ...this.defaultFormConfig,
                                };
                                this.goToStep('startingPoint');
                            },
                        },
                    },
                },

                settings: {
                    title: 'Create Form Template',
                    actions: {
                        nextAction: {
                            label: 'Next Step',
                            callback: () => {
                                this.saveForm();
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.goToStep(this.lastActiveStep);
                            },
                        },
                    },
                },
            },
        };
    },

    computed: {
        clientId() {
            return this.widget.clientId;
        },

        isClient() {
            return !!this.clientId;
        },

        isEditMode() {
            return this.widget.formId !== undefined && this.widget.formId !== null;
        },

        isFromBuilder() {
            return this.$route.params.formId !== undefined && this.$route.params.formId !== null;
        },

        isNextDisabled() {
            if (this.currentStep === 'settings' || this.currentStep === 'existingTemplate') {
                return this.formConfig.name.length <= 0;
            }
            return false;
        },

        emails() {
            return this.formConfig.emails.map((item) => item.name);
        },

        formId() {
            return this.widget.formId;
        },

        hasBackAction() {
            return this.steps[this.currentStep].actions.backAction !== undefined;
        },

        goBack() {
            return this.steps[this.currentStep].actions.backAction.callback;
        },

        goNext() {
            return this.steps[this.currentStep].actions.nextAction.callback;
        },

        accountId() {
            return this.$store.state.context.account.id;
        },

        widgetTitle() {
            if (this.isEditMode) {
                return 'Edit Form';
            }
            return this.steps[this.currentStep].title;
        },
    },

    created() {
        if (this.isEditMode) {
            return this.getEditFormData();
        }
        this.isLoading = true;
        this.getTotalFormsCount()
            .then((count) => {
                if (count === 0) {
                    this.isReturnDisabled = true;
                    return this.goToStep('settings');
                }
                this.selectedTemplateOption = this.templateOptions[0];
            })
            .catch((error) => {
                this.$helpers.showFailNotification('Failed to get total forms.', { error });
            })
            .finally(() => {
                this.isLoading = false;
            });
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goToStep(step) {
            this.lastActiveStep = this.currentStep;
            this.currentStep = step;
        },

        saveForm() {
            if (this.isEditMode) {
                this.confirmUpdate();
            } else {
                this.createForm();
            }
        },

        createForm() {
            this.isSaving = true;
            let payload = {
                Name: this.formConfig.name,
                Title: this.formConfig.title,
                Description: this.formConfig.description,
                Configuration: this.formConfig.configuration,
                AccountId: this.accountId,
                Categories: this.formConfig.categories,
            };

            if (this.isClient) {
                payload = {
                    ...payload,
                    Action: this.formConfig.action.name,
                    Message: this.formConfig.actionMessage,
                    SendAsPlainText: this.formConfig.isPlainTextNotification,
                    Emails: this.emails,
                    EmailSubject: this.formConfig.emailSubject,
                    Redirect: this.formConfig.actionRedirect,
                    ListIds: this.getListIds(this.formConfig.formSubmissionLists),
                };
            }

            FormService.post(payload)
                .then(({ data: id }) => {
                    this.$helpers.showSuccessNotification('Form created successfully');
                    this.closeWidget();
                    this.$router.push(`Forms/${id}/Design`);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred when creating form', {
                        error,
                    });
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        confirmUpdate() {
            const actions = {
                confirm: {
                    label: 'Continue',
                    action: () => this.updateForm(),
                },

                cancel: {
                    label: 'Cancel',
                },
            };

            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Are you sure you want to edit this form?',
                    content: '<p>All landing pages associated with this form will be updated.</p>',
                    actions,
                    confirmText: 'Continue',
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        updateForm() {
            this.isSaving = true;
            let changes = [
                {
                    name: 'Name',
                    value: this.formConfig.name,
                },
                {
                    name: 'Title',
                    value: this.formConfig.title,
                },
                {
                    name: 'Description',
                    value: this.formConfig.description,
                },
                {
                    name: 'Categories',
                    value: this.formConfig.categories,
                },
            ];

            if (this.isClient) {
                changes = [
                    ...changes,
                    {
                        name: 'Action',
                        value: this.formConfig.action.name,
                    },
                    {
                        name: 'Message',
                        value: this.formConfig.actionMessage,
                    },
                    {
                        name: 'SendAsPlainText',
                        value: this.formConfig.isPlainTextNotification,
                    },
                    {
                        name: 'Emails',
                        value: this.emails,
                    },
                    {
                        name: 'EmailSubject',
                        value: this.formConfig.emailSubject,
                    },
                    {
                        name: 'Redirect',
                        value: this.formConfig.actionRedirect,
                    },
                    {
                        name: 'ListIds',
                        value: this.getListIds(this.formConfig.formSubmissionLists),
                    },
                ];
            }

            FormService.patch(this.formId, changes)
                .then(({ data: id }) => {
                    this.$helpers.showSuccessNotification('Form updated successfully');
                    this.closeWidget();
                    if (!this.isFromBuilder) {
                        this.$router.push(`Forms/${id}/Design`);
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred when updating form', {
                        error,
                    });
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        getEditFormData() {
            this.isLoading = true;

            const requestConfig = { expand: 'ListForms($expand=List),Categories($select=Name)' };

            FormService.get(this.formId, requestConfig)
                .then((data) => {
                    this.setEditFormData(data);
                    this.goToStep('settings');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get the form data', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        setEditFormData({ data }) {
            const [item] = data.value;

            this.formConfig = {
                ...this.formConfig,
                name: item.Name,
                title: item.Title,
                description: item.Description,
                configuration: item.Configuration,
                categories: item.Categories.map(({ Name }) => Name),
            };

            if (this.isClient) {
                this.formConfig = {
                    ...this.formConfig,
                    actionMessage: item.Message,
                    actionRedirect: item.Redirect,
                    emailSubject: item.EmailSubject,
                    isPlainTextNotification: item.SendAsPlainText,
                    formSubmissionLists: item.ListForms
                        ? this.getFormLists(item.ListForms)
                        : this.defaultFormConfig.formSubmissionLists,

                    action: item.Action
                        ? this.getEditAction(item.Action)
                        : this.defaultFormConfig.action,

                    emails: JSON.parse(item.Emails)
                        ? this.getEditEmails(JSON.parse(item.Emails))
                        : this.defaultFormConfig.emails,
                };

                this.updatePlainTextOptions(item.SendAsPlainText);
            }
        },

        updatePlainTextOptions(value) {
            this.emailTextOptions = this.emailTextOptions.map((item) => {
                item.selected = item.name === 'Yes' ? value : !value;
                return item;
            });
        },

        getEditEmails(emails) {
            return emails.map((item) => {
                return {
                    id: this.$helpers.createId('email'),
                    name: item,
                };
            });
        },

        getEditAction(action) {
            return this.formOptions.find((option) => option.name === action);
        },

        getFormLists(listForms) {
            return listForms.map((list) => {
                return {
                    id: list.List.Id,
                    name: list.List.Name,
                    isMarketable: list.List.IsMarketable,
                };
            });
        },

        getListIds(lists) {
            return lists.map((list) => {
                return list.id;
            });
        },

        handleInputTemplateOption(newTemplateSelected) {
            this.selectedTemplateOption = newTemplateSelected;
        },

        getTotalFormsCount() {
            return new Promise((resolve, reject) => {
                FormService.getAll({
                    filter: `AccountId eq ${this.accountId}`,
                    count: true,
                })
                    .then(({ data }) => {
                        const totalTemplates = data['@odata.count'];
                        resolve(totalTemplates);
                    })
                    .catch(reject);
            });
        },
    },
};
</script>

<style>
.form-widget .widget-contents-container .content-wrapper {
    padding: 23px 29px 23px 33px;
}

.form-widget .widget-footer .transparent-btn {
    background: transparent;
    border: none;
    color: #877d88;
}

.form-widget .widget-footer .transparent-btn:hover {
    background: #e1e0e5;
}

.form-widget .widget-footer .actions-container {
    display: flex;
}

.form-widget .widget-footer .secondary-btn {
    background: transparent;
    border: 1px solid#18aeff;
    color: #18aeff;
    margin-right: 14px;
}

.form-widget .widget-footer .secondary-btn:hover {
    background: #dbedfa;
}

.form-widget .widget-footer .secondary-btn span.fa {
    margin-right: 10px;
}

.form-widget .widget-footer .app-button:not(.secondary-btn) span.fa {
    margin-left: 10px;
}
</style>
